/* eslint-disable max-lines-per-function */
import classNames from 'classnames'
import React, { useState } from 'react'
import dynamic from 'next/dynamic'
import { InView } from 'react-intersection-observer'
import ArrowDown from '@/assets/common/arrowDown.inline.svg'
import Link from '@/components/Common/Link'
// import data from './data.json'
import { TShareholdingPattern } from '@/components/Stocks/Widgets/types'
import InStockL2Heading from '@/components/Stocks/Pages/InStockDetailPageV2/InL2Heading'

const ChartLoader = () => (
  <div className="w-18 h-10 animate-pulse rounded bg-gray-100 " />
)
const Graph = dynamic(
  () =>
    import(
      '@/components/MutualFunds/Detail/CommonComponents/TraitTable/Widgets/Graph'
    ),
  { ssr: false, loading: ChartLoader },
)

export const ShareHoldingTable = ({
  table,
  sliceLength = table.tableContent.length,
}: {
  table: {
    tableHeading?: Array<string | JSX.Element>
    tableContent: Array<{
      columns?: Array<JSX.Element | string | number>
      url?: string
      backgroundColor?: string
    }>
    innerBorder?: boolean
    withoutOuterBorder?: boolean
  }
  sliceLength?: number
}) => {
  const [toggle, setToggle] = useState(false)
  const clickToggle = () => setToggle(prevState => !prevState)
  return (
    <div className=" my-0 overflow-x-auto">
      <table
        className={classNames('my-0 w-full ', {
          border: !table.withoutOuterBorder,
        })}
      >
        <thead>
          <tr className="bg-brand-grey-background">
            {table.tableHeading?.map((heading, index) => {
              return (
                <th
                  className={classNames(
                    'text-13px text-brand-grey-base break-words p-2 text-left font-normal',
                    { 'border-r': table.innerBorder },
                    { 'text-right': index !== 0 },
                    {
                      'bg-brand-grey-background sticky left-0 min-w-[140px] !p-1.5':
                        index == 0,
                    },
                  )}
                  key={index}
                >
                  {heading}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {table.tableContent
            .slice(0, toggle ? table.tableContent.length : sliceLength)
            .map((row, index) => {
              return (
                <tr
                  key={index}
                  style={{ backgroundColor: row.backgroundColor }}
                >
                  {row.columns?.map((data, i) => {
                    return (
                      <td
                        className={classNames(
                          'text-brand-black break-words border-b text-sm',
                          { 'border-r': table.innerBorder },
                          { 'text-right': i !== 0 },
                          { 'sticky left-0 bg-white': i == 0 },
                          { 'px-2 py-2.5': !row.url },
                        )}
                        key={i}
                        style={{ backgroundColor: row.backgroundColor }}
                      >
                        {row.url ? (
                          <Link
                            href={row.url}
                            className="block w-full px-2 py-3"
                          >
                            {data}
                          </Link>
                        ) : (
                          data
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
        </tbody>
      </table>
      {sliceLength < table.tableContent.length && (
        <div
          className="text-brand-blue-link my-2 flex cursor-pointer items-center justify-center text-center font-normal"
          onClick={clickToggle}
        >
          {toggle ? (
            <span className="flex items-center">
              See less{' '}
              <ArrowDown width={20} height={20} className="rotate-180" />
            </span>
          ) : (
            <span className="flex items-center">
              {' '}
              See all <ArrowDown width={20} height={20} />
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default React.memo(function ShareholdingPattern({
  data, // name,
  company_name,
}: {
  data: TShareholdingPattern[]
  company_name: string
  // name?: string
}) {
  const [year, setYear] = useState(data[0]?.as_of_year)

  const datum = data.find(item => item.as_of_year === year)

  const tabledata = {
    tableHeading: ['Investors', 'Holdings %', 'Quarterly Trend', '3M change'],
    tableContent:
      datum?.share_holders?.map(item => {
        return {
          columns: [
            item.share_holder_name ?? '',
            <div
              className="flex items-center justify-end gap-2"
              key={item.share_holder_name}
            >
              <div
                className="bg-brand-tertiary-blue h-1 w-10 rounded-full "
                key={item.share_holder_name}
              >
                <div
                  className="bg-brand-blue-azureRadiance h-1 rounded-full"
                  style={{ width: `${item.holding_percentage}%` }}
                />
              </div>
              <span className="w-20">{item.holding_percentage}%</span>
            </div>,
            <InView triggerOnce key={item.share_holder_name}>
              {({ inView, ref }) => (
                <div ref={ref}>
                  {inView && (
                    <Graph
                      key={item.share_holder_name}
                      data={{
                        graphPoints: item.historic_holdings?.map(
                          item => item.holding_percentage,
                        ),
                        graphFillColor:
                          (item.percentage_change_in_three_month ?? 0) < 0
                            ? '#FAEAEA'
                            : '#EBF4EC',
                        graphStrokeColor:
                          (item.percentage_change_in_three_month ?? 0) < 0
                            ? '#DF3C27'
                            : '#33A34D',
                      }}
                    />
                  )}
                </div>
              )}
            </InView>,
            <span
              key={item.share_holder_name}
              className={`${
                (item.percentage_change_in_three_month ?? 0) < 0
                  ? "text-brand-red-cinnabar  bg-brand-ter after:content-['%']"
                  : "text-brand-green-dark before:content-['+'] after:content-['%']"
              }`}
            >
              {(item.percentage_change_in_three_month ?? 0).toFixed(2)}
            </span>,
          ],
        }
      }) ?? [],
  }

  return (
    <div>
      {company_name && (
        <InStockL2Heading
          heading={`${company_name} Shareholding Pattern`}
          subheading={`View the shareholding pattern breakup of promoters, FIIs, DIIs, and retail investors in ${company_name}.`}
        />
      )}
      <div className="mb-7 mt-4 flex flex-wrap gap-2">
        {data.map(item => {
          return (
            <button
              className={classNames(
                'rounded-lg px-2 py-1 text-[13px] font-normal',
                {
                  'bg-brand-tertiary-blue text-brand-blue-azureRadiance':
                    year === item.as_of_year,
                  'bg-brand-grey-background border-brand-grey-base text-brand-grey-base ':
                    year !== item.as_of_year,
                },
              )}
              key={item.as_of_year}
              onClick={() => setYear(item.as_of_year)}
            >
              {item.as_of_year}
            </button>
          )
        })}
      </div>
      <ShareHoldingTable
        table={{ ...tabledata, innerBorder: false, withoutOuterBorder: true }}
      />
    </div>
  )
})
